export type Reference = {
  id: string;
  title: string;
  page: string;
  excerpt: string;
  sourceUrl?: string;
  contextualRelevancy?: 0 | 1 | 2 | 3 | 4 | 5;
};

export type Citation = {
  pointer: number;
  referenceIds: string[];
};

export type GenericAction = {
  type: string;
  label: string;
  parameters: {
    query: string;
  };
};

export type RelatedQuestionAction = {
  type: "submit-related-question";
  label: string;
  parameters: {
    query: string;
  };
};
export const isRelatedQuestionAction = (
  action: MessageAction,
): action is RelatedQuestionAction => action.type === "submit-related-question";

export type MessageAction = RelatedQuestionAction | GenericAction;

export type Message = {
  id: string;
  status: "optimistic" | "completed";
  value?: string;
  sender: "user" | "agent" | "system";
  create_time: string;
  thought?: boolean;
  citations?: Citation[];
  references?: Reference[];
  actions?: MessageAction[];
  appliedFilters?: FilterState;
};

export type ResearchId = string & { __brand: "ResearchId" };
export type Research = {
  id: ResearchId;
  display_name: string;
  status: "pending" | "completed" | "processing_meta_analysis" | "404";
  regenerating_summary?: boolean;
  knowledge_source: string;
  brief: string;
  questions: string;
  research_type?: string;
  create_time: string;
  update_time: string;
};

export type FilterName =
  | "creation_date"
  | "country"
  | "language"
  | "content_type"
  | "business_areas_of_focus"
  | "research_methodology"
  | "business_sector"
  | "keywords"
  | "contextual_relevancy"
  | "file_extension"
  | "current_turn_additional_exclusions"
  | "contextual_relevancy_docs";

export type MonthRange = {
  start?: {
    month?: string;
    year?: string;
  };
  end?: {
    month?: string;
    year?: string;
  };
};

export type FilterState = Partial<
  Omit<Record<FilterName, string[]>, "creation_date"> & {
    creation_date: MonthRange;
    custom_tags_filter: {
      key: string;
      values: string[];
    }[];
  }
>;

export type TermsStatus =
  | "loading"
  | "pending_acceptance"
  | "accepted"
  | "error"
  | "acceptance_not_required";

export interface AppState {
  authState: "loading" | "authenticated" | "unauthenticated";
  /**
   * List of researches.
   */
  researchMap: {
    items: Record<ResearchId, Research>;
    loading: boolean;
  };
  /**
   * List of messages for each research.
   */
  messages: {
    items: Record<ResearchId, Message[]>;
    loading: boolean;
  };
  /**
   * List of the latest deduplicated file references for each research.
   * This is not the same as the references in the messages, as those include the specific excerpts.
   */
  fileReferences: {
    items: Record<ResearchId, Pick<Reference, "id" | "title">[]>;
    loading: boolean;
  };

  filters: {
    items: Record<ResearchId, FilterState>;
    loading: boolean;
  };

  filterOptions: {
    /**
     * Legacy filter options format
     * @deprecated on August 2025
     */
    items?: Record<FilterName, { value: string[] }>;
    /**
     * New filter options format
     * valid from August 2025
     */
    list?: {
      label: string;
      key: string;
      type: "selection_multiple" | "month_range";
      custom: boolean;
      options?: {
        label: string;
        value: string;
      }[];
    }[];
    loading: boolean;
  };

  researchTypes: {
    items: {
      name: string;
      description: string;
      display_name: string;
    }[];
    loading: boolean;
  };

  /**
   * List of knowledge sources available to the user.
   */
  knowledgeSources: {
    items: { name: string; repoUrl?: string }[];
    loading: boolean;
  };

  /**
   * Terms and conditions state.
   */
  termsAndConditions: {
    status: TermsStatus;
    content: string | null;
    loading: boolean;
    accepting: boolean;
    error: string | null;
  };
}
